import { Link } from "@/components/ui/typography";

export function Terms() {
  return (
    <div className="markdown">
      <p>
        Welcome to Graze! These Terms of Service (or “<strong>Terms</strong>”)
        are a binding contract between Graze Social PBC (“<strong>Graze</strong>
        ”) and you and, and, together with our{" "}
        <Link to="/privacy-policy">Privacy Notice</Link>, govern your use of the
        Graze platform, our website <Link to="/">graze.social</Link>, and the
        products and services we offer (collectively, our “
        <strong>Services</strong>”). Any capitalized terms we use in these Terms
        without defining them have the definitions given to them in our{" "}
        <Link to="/privacy-policy">Privacy Notice</Link>. Additional and
        separate terms may apply to certain Services, each of which will be
        considered to form part of these Terms. If you have any questions about
        these Terms, please contact{" "}
        <a href="mailto:legal@graze.social">legal@graze.social</a>.{" "}
      </p>
      <p>
        THESE TERMS INCLUDE A CLASS ACTION WAIVER AND AN ARBITRATION PROVISION
        THAT GOVERNS ANY DISPUTES BETWEEN YOU AND GRAZE. Section headings and
        bullet summaries are for the convenience of the parties and do not
        affect the meaning or interpretation of the agreement.
      </p>
      <ol>
        <li>
          <strong>ELIGIBILITY &amp; ACCEPTANCE</strong>{" "}
          <ul>
            <li>You must be a legal adult to use the Services. </li>
            <li>
              By using the Services or clicking “Accept,” you agree to these
              Terms in their entirety.{" "}
            </li>
            <li>You accept these Terms for yourself and your Entity.</li>
          </ul>
          <p>
            To be eligible to use the Services, you must be at least 18 years
            old or the legal age of majority in your jurisdiction, have the
            legal authority to enter into a binding contract, and have never
            been previously suspended or removed from the Services.
          </p>
          <p>
            You accept the Terms by clicking to accept or agree to the Terms
            where this option is made available to you in any agreement,
            electronic form, or user interface, or by simply accessing the
            Services. BY ACCESSING THE SERVICES, YOU AGREE TO BE UNCONDITIONALLY
            BOUND BY THESE TERMS. If you access the Services on behalf of a
            company or other entity (“
            <strong>Entity</strong>”), you represent, agree, and warrant that
            you are authorized to act on behalf of the Entity and to bind such
            Entity to these Terms. These Terms will remain in effect as long as
            you use the Services. If you do not agree with any of these Terms,
            do not access the Site or use the Services.
          </p>
        </li>
        <li>
          <strong>REGISTRATION</strong>{" "}
          <ul>
            <li>
              You must register and create an account. You are responsible for
              your account.{" "}
            </li>
          </ul>
          <p>
            You must first register with us and create an account to use the
            Services. By registering, you agree to provide Graze with accurate
            and complete information. The information we collect during
            registration and how we use that information is described in our
            <Link to="/privacy-policy">Privacy Notice</Link>. You alone are
            permitted to access the Services via your account. Your credentials
            are for your individual use only and shall not, under any
            circumstances, be shared, distributed, or otherwise made available
            to any person or entity. You accept responsibility for all activity
            that occurs under your account and password. You may not use false
            identities or impersonate any other person or use credentials that
            you are not authorized to use. You are solely responsible for
            ensuring the security and confidentiality of your login credentials
            and account. We are not liable for any loss or damage arising out of
            your failure to maintain your account security. You agree to notify
            Graze immediately of any suspected theft, loss, or fraudulent use of
            your login or password.{" "}
          </p>
        </li>
        <li>
          <strong>SERVICES</strong>{" "}
          <ul>
            <li>Graze grants you a limited license to use the Services. </li>
            <li>We offer free services and paid services. </li>
            <li>
              <p>
                You may post Submissions, and you grant us a license to use your
                Submissions.{" "}
              </p>
            </li>
          </ul>
          <ol>
            <li>
              <strong>Ownership.</strong> The Services and contents thereof
              (e.g., all software, designs, trademarks, and content) are
              protected by U.S. and foreign copyright, trademark, trade dress,
              or other proprietary right laws and international conventions.
              Neither these Terms nor your use of the Services transfers any
              right, title, or interest in the Services to you. All rights not
              expressly granted under these Terms are reserved by Graze. Graze
              and the other licensors of the trademarks in our Services reserve
              all rights with respect thereto. You may not use any trademarks on
              the Services without prior express written permission. Only a duly
              authorized officer of Graze may grant permission or a license to
              use the Services; any attempted grant or similar promise by anyone
              other than a duly authorized officer of Graze is invalid.{" "}
            </li>
            <li>
              <strong>License Grant.</strong> Graze hereby grants you a limited,
              revocable, non-exclusive, non-transferable right during the term
              of these Terms to use the Services solely in connection with your
              internal business operations and only in accordance with the
              access and use parameters of the Services you have signed up to
              use and, if Paid Services, paid the fees in full. You may only
              display, download, or print the Services for the purpose of using
              the Services as an internal or personal business resource. The
              Services are licensed not sold.{" "}
            </li>
            <li>
              <strong>Free Services.</strong> A registered user (“
              <strong>User</strong>”) may use some of the Services free of
              charge, as described in this paragraph (b) and on{" "}
              <a href="https://www.graze.social/">graze.social</a>.{" "}
              <ol>
                <li>
                  <strong>Feeds.</strong> Graze provides tools that enable users
                  and companies (“<strong>Feed Curators</strong>”) to create and
                  monetize custom content feeds (“<strong>Feeds</strong>”) for
                  the AT Protocol. Our platform allows you to build algorithms
                  to filter content from the Bluesky firehose (“
                  <strong>Bluesky Content</strong>”). Feed Curators create an
                  account on the Graze platform using their Bluesky credentials
                  and an app-specific password (“
                  <strong>Curator Account</strong>”). Within the Graze platform,
                  Feed Curators set the name, description, and avatar (“
                  <strong>Public Feed Data</strong>”) and configure the feed
                  based on the characteristics of the Bluesky Content and the
                  accounts that post it (“<strong>Feed Definition</strong>”).
                  The feed can either be published to Bluesky publicly, or to
                  members of a list of Bluesky users that is managed by the Feed
                  Curator (“<strong>Subscriber List</strong>”) within the Graze
                  platform. The Graze platform displays a list of all public
                  Feeds created using Graze, along with information about the
                  number of views and posts (“<strong>Feed Statistics</strong>
                  ”). We reserve the right to block, remove, and/or permanently
                  delete your Feed if we determine that your Feed or any content
                  on your Feed is in breach of these Terms, our Community
                  Guidelines or other policies, or applicable law or if we
                  determine it creates risk for Graze or negatively impacts the
                  experience or interests of others.{" "}
                </li>
                <li>
                  <strong>Community.</strong> Graze provides multiple channels
                  to facilitate feedback and support including a Discord server,
                  a GitHub repository, and others (“
                  <strong>Graze Community Channels</strong>”). By posting,
                  submitting, or sharing any content, ideas, suggestions,
                  feedback, reviews, or materials (“<strong>Submissions</strong>
                  ”) via the Graze Community Channels or directly to Graze, you
                  grant us a worldwide, perpetual, irrevocable, royalty-free,
                  and fully transferable license to use, modify, adapt, publish,
                  distribute, and commercialize your Submissions in any manner,
                  without compensation or credit to you. Additionally, you
                  acknowledge and agree that: (i) you waive any moral rights or
                  claims related to your Submissions; (ii) Graze has no
                  obligation to keep your Submissions confidential or to credit
                  you; and (iii) if we use, develop, or implement any idea
                  similar to a Submission, you have no claim to ownership,
                  compensation, or recognition. If you disagree with the
                  foregoing terms, do not post or send Submissions.{" "}
                </li>
              </ol>
            </li>
            <li>
              <strong>Paid Services.</strong> Graze offers certain Services to
              Users in exchange for a subscription fee or other payment (“
              <strong>Paid Services</strong>”), such as an advertising
              marketplace, sponsored content, or other services, products, or
              features as we make available from time to time. A full
              description of Paid Services and their associated fees is
              available on <a href="https://www.graze.social/">graze.social</a>.{" "}
              <ol>
                <li>
                  <strong>Advertising Marketplace.</strong> Through the
                  Advertising Marketplace, Graze will deliver instances of a
                  promoted post delivered to Bluesky during a campaign (“
                  <strong>Impressions</strong>”) to the User operating the feed
                  where the promoted posts will be displayed (“
                  <strong>Curator</strong>”) as part of their feed in accordance
                  with parameters for the total set of impressions purchased by
                  the Sponsor (“<strong>Campaign</strong>”) as set by the
                  Sponsor. Curators are strictly prohibited from manipulating,
                  astro-turfing, or fraudulently increasing view counts in any
                  manner. Graze reserves the right to log and analyze forensic
                  data for auditing purposes to detect and prevent such
                  activities. Curators will only receive payment for Impressions
                  that are verified as displayed on the Graze platform. The User
                  paying to promote posts into a feed on a per-impressions (CPM)
                  basis (“<strong>Sponsor</strong>”) must pay for sponsored
                  content in advance or as otherwise specified in the Services.
                  Sponsors are responsible for ensuring that all sponsored
                  content complies with these Terms, our Community Guidelines,
                  and all applicable laws. Graze reserves the right to reject,
                  remove, or demonetize any sponsored content that does not
                  comply. Graze does not guarantee specific performance metrics
                  (e.g., clicks, impressions, or engagement) for sponsored
                  content. Sponsors are responsible for monitoring the
                  performance of their campaigns and making adjustments as
                  needed.
                  <p>
                    The minimum transaction spend is $25.00 USD. Payments for
                    sponsored content are divided as follows: 70% to the Curator
                    and 30% to Graze as a platform fee. Graze covers all
                    transaction costs associated with the Sponsor payment
                    process. Payments will be made to Curators on a rolling
                    30-day basis following the completion of Impressions for a
                    Campaign. Curators will be responsible for any bank fees
                    associated with receiving payment.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <strong>Availability.</strong> We will use commercially reasonable
              efforts to maintain availability of the Services. You understand
              and agree that there will be times when the Services will not be
              available, such as scheduled maintenance times, outages, emergency
              maintenance, unavailability caused by software, hardware, or other
              users, and causes beyond our reasonable control. We will make
              commercially reasonable efforts to notify you of planned downtime
              and unavailability of the Services. We are not liable for any
              delays, interruptions, or other transmission errors resulting from
              any lack of service access or caused by your device or your
              internet or wireless service provider.{" "}
            </li>
            <li>
              <strong>Updates.</strong> Graze is actively developing new
              features and products to improve the Services. From time to time,
              we may, in our sole discretion, develop and provide updates to the
              Services, which may include upgrades, bug fixes, patches, other
              error corrections, and/or new features (collectively, “
              <strong>Updates</strong>”). Updates may add or remove features,
              start offering new services, or stop offering some Services
              entirely. You agree that we have no obligation to provide any
              Updates or to continue to provide or enable any features. Based on
              your device settings, when an internet-connected device is
              connected to the internet, either (i) the Updates will
              automatically download and install; or (ii) you may receive notice
              of or be prompted to download and install available Updates. You
              agree to promptly download and install all Updates and acknowledge
              and agree that our Services or portions thereof may not properly
              operate should you fail to do so. You further agree that all
              Updates will be deemed part of the Services and be subject to
              these Terms.{" "}
            </li>
          </ol>
        </li>

        <li>
          <strong>FEES &amp; PAYMENT</strong>{" "}
          <ul>
            <li>
              You agree to pay the Fees you incur, and you authorize us to
              charge your payment method.{" "}
            </li>
            <li>
              Subscription Services are automatically billed on the Subscription
              Billing Date.
            </li>
          </ul>
          <ol>
            <li>
              <strong>Fees.</strong> You agree to pay Graze all fees and other
              amounts due (“<strong>Fees</strong>”) as the result of your use of
              the Services. All Fees are stated in U.S. Dollars. You are
              responsible for all applicable taxes and other fees associated
              with your use of the Paid Services.{" "}
            </li>
            <li>
              <strong>Payment</strong> <strong>Authorization</strong>. You
              hereby expressly authorize Graze to charge all Fees owed by you,
              including all applicable taxes, to the payment method you provide
              to Graze. If you pay via credit card, Graze may seek
              pre-authorization of your credit card account prior to your
              purchase to verify that the credit card is valid and has the
              necessary funds or credit available to cover your Fees.{" "}
            </li>
            <li>
              <strong>
                Subscriptions<em>.</em>
              </strong>{" "}
              The Services may be offered in exchange for automatically
              recurring payments for periodic charges (“
              <strong>Subscription Service</strong>”). If you activate a
              Subscription Service, you expressly authorize Graze to
              periodically charge your payment method, on a going-forward basis
              and until cancellation of either the recurring payments or your
              account, all accrued Fees on the Subscription Billing Date all
              applicable fees and taxes for the next subscription period. The “
              <strong>Subscription Billing Date</strong>” is the date when you
              first purchase a subscription to the Services. The subscription
              will continue unless and until you cancel the subscription or we
              terminate it. You must cancel any subscription before it renews to
              avoid billing of the next periodic Subscription Fees to your
              payment method. Graze will bill the periodic Subscription Fees to
              the payment method you provide.{" "}
            </li>
            <li>
              <strong>Refunds.</strong> Graze will only refund payments for a
              Campaign to a Sponsor if requested prior to the Campaign
              displaying any Impressions. Once a Campaign has begun, refunds
              will be prorated based on the number of Impressions that have
              already been displayed. Any bank or transaction fees associated
              with a Sponsor’s initial payment will not be refunded. Payments
              may not be refunded if the sponsored content is rejected or
              removed by Graze for violating these terms. Graze will actively
              dispute all chargebacks filed by Sponsors. Graze will not process
              any refunds except as expressly provided in this paragraph (d).{" "}
            </li>
            <li>
              <strong>Delinquent Payment.</strong> Graze may suspend or
              terminate access to the Services if any Fees due remain unpaid. In
              addition to the amount due for the Services, a delinquent account
              will be charged with fees or charges that are incidental to any
              charge back or collection of any unpaid amount, including
              collection fees.{" "}
            </li>
          </ol>
        </li>
        <li>
          <strong>ACCEPTABLE USE</strong>{" "}
          <ul>
            <li>
              You agree to use the Services in compliance with our Community
              Guidelines and the acceptable use requirements outlined below.{" "}
            </li>
            <li>
              You must notify legal@graze.social if you become aware of any
              unauthorized use.
            </li>
          </ul>
          <ol>
            <li>
              <strong>Use Requirements.</strong> You agree to use the Services
              in compliance with these Terms, our Community Guidelines and other
              posted policies, and all applicable laws. The Services are made
              available using Bluesky’s API. By using the Services, you agree to
              comply with and be bound by{" "}
              <a href="https://bsky.social/about/support/tos">
                Bluesky’s Terms of Service
              </a>
              . Additionally, you agree to not use the Services in a manner
              that:{" "}
              <ul>
                <li>
                  Violates the{" "}
                  <a href="https://bsky.social/about/support/community-guidelines">
                    Bluesky Community Guidelines
                  </a>
                  ;{" "}
                </li>
                <li>
                  Is illegal, harmful, abusive, defamatory, harassing, or
                  discriminatory;{" "}
                </li>
                <li>
                  Infringes on the intellectual property rights of others;{" "}
                </li>
                <li>
                  Impersonates others or misrepresents your affiliation with any
                  entity;{" "}
                </li>
                <li>Contain malware, viruses, or other harmful code; or </li>
                <li>
                  Promote or facilitate violence, hate speech, or illegal
                  activities.
                </li>
              </ul>
              <p>
                Your use of the Services in any manner that violates the
                foregoing will be deemed a material breach of these Terms.{" "}
              </p>
            </li>
            <li>
              <strong>Protection Against Unauthorized Use.</strong> You agree to
              use reasonable efforts to prevent any unauthorized use of the
              Services and to promptly notify Graze at legal@graze.social if any
              unauthorized use comes to your attention. If there is unauthorized
              use by anyone who obtained access to the Services directly or
              indirectly through your account, you agree to take all steps
              reasonably necessary to terminate the unauthorized use. You will
              cooperate and assist with any actions taken by Graze to prevent or
              terminate unauthorized use of the Services. While Graze may review
              reports, we do not guarantee action in response to every report.{" "}
            </li>
            <li>
              <strong>Use Restrictions.</strong> Except as otherwise explicitly
              provided in these Terms or as may be expressly permitted by
              applicable law, you agree to not, and will not permit or authorize
              third parties to: (i) use the Services for any illegal purpose or
              in violation of any local, state, national, or international law;
              (ii) violate any right of a third party, including by infringing
              or misappropriating any third-party intellectual property right;
              (iii) rent, lease, or otherwise permit third parties to use the
              Services; (iv) use the Services to provide services to third
              parties (e.g., as a service bureau); (v) use the Services for any
              benchmarking activity or in connection with the development of any
              competitive product; (vi) modify, translate, reverse engineer,
              decompile, disassemble, or otherwise derive the source code or the
              underlying ideas, algorithms, structure, or organization of the
              Services (except to the extent that applicable law prevents the
              prohibition of such activities); (vii) use or access the Services
              in a manner that materially impacts or burdens Graze or our
              servers and other computer systems, or that interferes with our
              ability to make available any product or service to any third
              party; (viii) perform any fraudulent activity including
              impersonating any person or entity, claiming a false affiliation,
              or accessing any other account on the Services without permission;
              (ix) circumvent or disable any security or other technological
              features or measures of the Services; or (x) attempt to circumvent
              your permitted access to Services or use parameters, including
              (but not limited to) via the creation of multiple accounts using
              different email addresses. If Graze, in its sole and reasonable
              discretion, suspects that Subscriber has breached any of the
              acceptable use requirements of this Section 6, it may immediately
              terminate these Terms and Subscriber’s subscription to the
              Services.{" "}
            </li>
          </ol>
        </li>
        <li>
          <strong>USER CONTENT</strong>{" "}
          <ul>
            <li>
              You own and are responsible for your User Content and you grant us
              a license to use it.{" "}
            </li>
          </ul>
          <p>
            You retains all right, title, and interest, including all
            Submissions, data, information, or other content that you disclose
            or otherwise make available to Graze through the Services (“
            <strong>User Content</strong>
            ”). You hereby grant Graze an irrevocable, unlimited, non-exclusive,
            worldwide, perpetual, royalty-free, fully paid, sublicensable, fully
            transferable license to modify, adapt, publish, distribute, process,
            transmit, create derivatives of, commercialize or otherwise use for
            any purpose your User Content in perpetuity. By providing User
            Content to Graze, you represent and warrant that: (a) you have all
            rights necessary to grant Graze the licenses set forth in in this
            Section and to enable Graze to exercise its rights under these
            Terms; (b) your collection, use, and disclosure of the User Content
            complies with all applicable privacy and data protection laws,
            rules, and regulations (collectively, “<strong>Privacy Laws</strong>
            ”); and (c) the User Content and the use thereof as contemplated by
            these Terms, does not and will not: (i) infringe, violate, or
            misappropriate any third-party right, including any copyright,
            trademark, patent, trade secret, moral right, privacy right, right
            of publicity, or any other intellectual property or proprietary
            right; or (ii) cause Graze to violate any Privacy Laws or any other
            law or regulation. You are solely responsible for maintaining and
            backing up your User Content.
          </p>
        </li>
        <li>
          <strong>DATA PROTECTION</strong>{" "}
          <ul>
            <li>
              You consent to our privacy practices as described in our Privacy
              Notice.{" "}
            </li>
            <li>
              You will not include legally protected data in your User Content
              unless we agree in writing.
            </li>
          </ul>
          <p>
            Graze treats all personal information processed through the Services
            in accordance with our{" "}
            <Link to="/privacy-policy">Privacy Notice</Link>, incorporated
            herein by this reference. By using Services in any manner, you
            consent to the Graze
            <Link to="/privacy-policy">Privacy Notice</Link> and any additional,
            separate <Link to="/privacy-policy">Privacy Notice</Link>s or
            disclosures applicable to the Services you use. Graze implements and
            maintains commercially reasonable organizational and technical
            measures that are designed to prevent unauthorized or unlawful
            access, use or disclosure of data on the Services.
          </p>
          <p>
            Except as otherwise agreed in writing with graze, you understand
            that you are strictly prohibited from including any data in your
            User Content that is protected as “personal information” or similar
            under applicable Privacy Laws or that is otherwise considered
            sensitive, confidential, protected, or subject to limitations or
            prohibitions of use under applicable laws.{" "}
          </p>
        </li>
        <li>
          <strong>AI TECHNOLOGY</strong>
          <p>
            Graze uses artificial intelligence, machine learning, and related
            technologies (“<strong>AI Technology</strong>”) to categorize and
            process data as part of the Services. You hereby authorize Graze to
            use AI Technology to process your User Content, provide the
            Services, produce derivative data, or for any other lawful purpose.
            You acknowledge and agree that, due to the nature of artificial
            intelligence and machine learning, results of using by AI Technology
            may vary or may contain inaccuracies. You release the Graze
            Indemnitees from any claims or liability related to use of AI
            Technology.
          </p>
        </li>
        <li>
          <strong>THIRD-PARTY SERVICES</strong>
          <ul>
            <li>
              We are not responsible for Third-Party Services that may be
              integrated in the Services.
            </li>
          </ul>
          <p>
            The Services may integrate with third-party services such as
            Bluesky, Discord, GitHub, Stripe, or other third-party or
            open-source materials, technology, tools, or features (“
            <strong>Third-Party Services</strong>
            ”). You agree that such Third-Party Services may have access to your
            User Content as part of provision of the Services. Third-Party
            Services are not under Graze’s control, and, to the fullest extent
            permitted by law, Graze is not responsible for any activities by or
            outcomes of using a Third-Party Service, including the removal or
            restriction of your access or User Content. Additionally, Graze
            shall have no liability to you whatsoever in the event Third-Party
            Services become unavailable or unusable for any reason or otherwise
            reduce or eliminate our ability to provide you with the Services.
          </p>
        </li>
        <li>
          <strong>INDEMNIFICATION</strong>
          <ul>
            <li>You agree to indemnify us.</li>
          </ul>
          <p>
            You agree to indemnify, defend (at our option), and hold harmless
            Graze and its owners, officers, directors, shareholders, employees,
            and agents (the “<strong>Graze Indemnities</strong>”) and their
            respective suppliers, licensors and partners from and against any
            and all claims, losses, damages, liabilities and expenses, including
            legal fees and expenses, resulting from or arising out of your: (i)
            violation of these Terms; (ii) negligent, willful misconduct, fraud,
            or strict liability; (iii) your Submissions or any use, misuse
            and/or access of the Services; (iv) violation of any applicable law;
            (v) your activity or content on any third-party platforms or through
            any Third-Party Service; and/or (vi) violation of the rights of any
            third party, or any breach of the representations, warranties, and
            covenants made by you herein. We reserve the right, at your expense,
            to assume the exclusive defense and control of any matter for which
            you are required to indemnify Graze, and you agree to cooperate with
            Graze’s defense of these claims, at your sole cost and expense, and
            such amounts shall be paid to us on demand in immediately payable
            funds.
          </p>
        </li>
        <li>
          <strong>LIMITATION OF LIABILITY</strong>
          <ul>
            <li>Our potential liability under these Terms is limited.</li>
          </ul>
          <p>
            IN NO EVENT WILL GRAZE OR THE GRAZE INDEMNITEES BE LIABLE FOR
            DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN
            CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE SERVICES, ANY
            THIRD-PARTY SERVICES, ANY CONTENTS OR THE CONTENT OF THIRD-PARTY
            SERVICES, OR ANY USER CONTENT OR OTHER MATERIALS VIEWED OR OBTAINED
            THROUGH THE SERVICES, OR THE ACTION OR INACTION OF ANY USER OR THIRD
            PARTY, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL,
            CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO,
            PERSONAL INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF
            REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR ANTICIPATED SAVINGS,
            LOSS OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY
            TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE, EVEN
            IF FORESEEABLE. IN NO EVENT WILL OUR AGGREGATE LIABILITY ARISING OUT
            OF OR RELATED TO THESE TERMS, WHETHER ARISING OUT OF OR RELATED TO
            BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE) OR OTHERWISE, EXCEED
            THE TOTAL OF THE AMOUNTS PAID BY YOU TO US DURING THE LAST THREE (3)
            MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
          </p>
        </li>
        <li>
          <p>
            <strong>DISPUTE RESOLUTION</strong>
          </p>
          <ul>
            <li>
              <p>
                You will attempt to resolve Disputes informally before bringing
                a formal claim.
              </p>
            </li>
            <li>
              <p>All Disputes will be resolved by arbitration. </p>
            </li>
            <li>
              <p>
                You will not bring or participate in a class action against us.
              </p>
            </li>
            <li>
              <p>Claims must be brought within 1 year of the date it arose.</p>
            </li>
          </ul>
          <p>
            <strong>
              READ THIS SECTION CAREFULLY. IT MAY SIGNIFICANTLY AFFECT YOUR
              LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.
            </strong>{" "}
            You agree that any dispute between you and Graze arising out of or
            relating to the Services or these Terms (collectively, “
            <strong>Disputes</strong>”) will be governed by this Section 10.
            This Section 10 shall not apply if and to the extent prohibited by
            the laws governing your jurisdiction.{" "}
          </p>
          <ol>
            <li>
              <strong>Informal Dispute Resolution</strong>. We want to address
              your concerns without the necessity of a formal legal case. Before
              filing a claim against Graze, you agree to try to resolve the
              Dispute informally by contacting legal@graze.social. Graze will
              contact you by email as part of a good faith effort to resolve the
              Dispute informally. If a Dispute is not resolved within 30 days
              after submission, you or Graze may bring a formal proceeding.{" "}
            </li>
            <li>
              <strong>Arbitration Agreement</strong>. You and Graze each agree
              to resolve any Disputes through final and binding arbitration.
              JAMS will administer the arbitration and determine payment of fees
              under its streamlined rules. The arbitration will be held in
              Portland, Oregon, United States or any other location to which we
              agree. Notwithstanding the foregoing, either you or Graze may
              assert claims, if they qualify, in small claims court in Portland,
              Oregon or any United States county where you are located. Either
              party may bring a lawsuit solely for injunctive relief to stop
              unauthorized use or abuse of the Services or infringement of
              intellectual property rights (for example, trademark, trade
              secret, copyright, or patent rights) without first engaging in
              arbitration or the informal dispute resolution process described
              above.{" "}
            </li>
            <li>
              <strong>Class Action Waiver.</strong> You may only resolve
              Disputes with Graze on an individual basis and may not bring a
              claim as a plaintiff or a class member in a class, consolidated,
              or representative action. Class arbitrations, class actions,
              private attorney general actions, and consolidation with other
              arbitrations are not allowed under these Terms.{" "}
              <strong>
                YOU AND GRAZE AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER
                ONLY IN YOUR OR OUR INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF
                OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE
                PROCEEDING.
              </strong>{" "}
            </li>
            <li>
              <strong>Waiver of Jury Trial.</strong> To the extent that the
              Arbitration Agreement herein does not apply or is held
              unenforceable, each party acknowledges and agrees that any
              controversy that may arise under these Terms, including exhibits,
              schedules, attachments, and appendices attached to these Terms, is
              likely to involve complicated and difficult issues and, therefore,
              each such party irrevocably and unconditionally waives any right
              it may have to a trial by jury in respect of any legal action
              arising out of or relating to these Terms, including any exhibits,
              schedules, attachments or appendices attached to these Terms, or
              the transactions contemplated hereby.{" "}
            </li>
            <li>
              <strong>Limitation on Claims; Relief.</strong> Regardless of any
              statute or law to the contrary, any claim or cause of action
              arising out of or related to your use of the Services must be
              filed within one (1) year after such claim or cause of action
              arose, or else that claim, or cause of action will be barred
              forever. You agree that the remedy at law for any breach or
              threatened breach of these Terms by you would, by its nature, be
              inadequate, and that Graze may be entitled, in addition to
              damages, to a restraining order, temporary or permanent injunctive
              relief, specific performance, and other appropriate equitable
              relief, without showing or providing that any monetary damage has
              been sustained. In the event of any dispute between the parties
              concerning the terms and provisions of these Terms, Graze shall be
              entitled to collect from you all costs incurred in such dispute,
              including reasonable attorneys’ fees, if we are the prevailing
              party.{" "}
            </li>
          </ol>
        </li>
        <li>
          <strong>GENERAL</strong>{" "}
          <ul>
            <li>
              These Terms (with the documents referenced in these Terms) are the
              entire agreement between you and us with respect to the Services.{" "}
            </li>
            <li>
              You agree to use the Services in compliance with U.S. export
              control laws.{" "}
            </li>
            <li>
              You may not assign these Terms to anyone else without our consent.{" "}
            </li>
          </ul>
          <ol>
            <li>
              <strong>Geographic Limitations.</strong> The Services is provided
              from Graze’s headquarters in the United States. Graze makes no
              representation that the Services comply with legal or regulatory
              requirements of jurisdictions outside the United States. If you
              use the Services from other jurisdictions, such use is at your own
              initiative, and you are solely responsible for compliance with
              applicable local laws. You acknowledge that you may not be able to
              use the Services in whole or in part from outside of the United
              States and that access thereto may not be legal by certain persons
              or in certain countries. You agree to comply with all applicable
              local, state, federal, and foreign laws in using the Services. You
              are solely responsible for notifying Graze if your use of the
              Services or these Terms violates any law, regulation, or rule of
              your locality. Use of the Services from jurisdictions where such
              access is illegal is prohibited. Graze is not responsible for any
              violation of law in relation to your use of the Services.{" "}
            </li>
            <li>
              <strong>Governing Law</strong>. Except as otherwise required by
              applicable law, the Terms and the resolution of any Disputes shall
              be governed by and construed in accordance with the laws of the
              State of Delaware without regard to its conflict of laws
              principles. The Federal Arbitration Act, Delaware state law, and
              applicable U.S. federal law, without regard to the choice or
              conflicts of law provisions, will govern these Terms. Foreign laws
              do not apply. The United Nations on Contracts for the
              International Sale of Goods and any laws based on the Uniform
              Computer Information Transactions Act (UCITA) shall not apply to
              these Terms. You hereby consent to the exclusive jurisdiction of
              the state and federal courts located in the state of Delaware, USA
              to enforce these Terms or adjudicate any other Dispute, and you
              and Graze each agree to waive the right to a jury trial. Except as
              otherwise required by applicable law, if the agreement to
              arbitrate is found not to apply to a claim, you and Graze agree
              that any judicial proceeding (other than small claims actions)
              will be brought in the federal or state courts in Delaware.{" "}
            </li>
            <li>
              <strong>Export Controls.</strong> You agree to use the Services in
              compliance with U.S. export control laws, and that you will not
              export the Services or any of its code, data, or other components
              to countries, persons or entities prohibited by such laws. You
              also agree to not submit the Services to any government agency for
              licensing consideration or other regulatory approval without our
              prior written consent.{" "}
            </li>
            <li>
              <strong>Entire Agreement.</strong> These Terms and the documents
              incorporated herein by reference are the entire agreement between
              you and Graze with respect to the Services and replace and
              supersede all prior and contemporaneous understandings and
              agreements related thereto.{" "}
              <a href="https://www.lawinsider.com/clause/headings">Section</a>{" "}
              headings are inserted for convenience only and shall not affect in
              any way the meaning or interpretation of these Terms. Additional
              agreements or documents may apply to certain features of the
              Services or other services we offer, which become part of these
              Terms. If these Terms are inconsistent with any other agreements
              or documents, the agreements or documents relating to the specific
              feature or service will govern. Nothing in these Terms will be
              deemed to confer any third-party rights or benefits. You agree
              that no joint venture, partnership, employment, or agency
              relationship exists between you and Graze as a result of these
              Terms or your use of the Services.{" "}
            </li>
            <li>
              <strong>Term; Termination.</strong> These Terms are in effect from
              the first date you download the Services until you discontinue all
              use of the Services and delete your account. Upon deletion of your
              account, you must immediately discontinue use of the Services and
              immediately uninstall and delete all copies of the Services from
              your device. Immediately upon termination of these Terms as
              described in this paragraph, all license and rights granted to you
              under these Terms automatically terminate and you shall
              automatically forfeit the right to use the Services and all
              outstanding payment obligations of Subscriber become due and
              payable immediately. Your obligation to pay accrued fees will
              survive any termination of these Terms. We may terminate your
              account and/or these Terms immediately with or without notice to
              you if you breach any of these Terms or for any reason in our sole
              discretion. Our rights and your obligations to Graze under these
              Terms shall not be affected by the deletion of your account or
              termination of these Terms. All provisions of these Terms that, by
              their nature, should survive any termination or expiration of
              these Terms shall survive any termination or expiration.{" "}
            </li>
            <li>
              <strong>Enforcement.</strong> Graze has the right to investigate
              violations of these Terms and any conduct that affects the
              Services, and in response may take any action we may deem
              appropriate. Graze reserves the right (but is not required) to
              remove or disable your access to the Services at any time and
              without notice, and at our sole discretion if we determine that
              your use thereof is objectionable or in violation of these Terms.{" "}
            </li>
            <li>
              <strong>Assignment.</strong> We may assign these Terms, in whole
              or in part, to any person or entity at any time without your
              consent. You may not assign these Terms without our prior written
              consent, and any unauthorized assignment by you shall be
              automatically null and void.{" "}
            </li>
            <li>
              <strong>Notice.</strong> We may provide you with notice by any
              means, including (without limitation) via postings on the Services
              or via email, of changes to these Terms. Notices given via the
              Services are deemed given the first time you access the Services
              after such notice is posted. Notices emailed to you will be deemed
              given and received when the email is sent. A notice that we give
              is deemed given even if it is intercepted by your spam filter and
              not actually read. If you don’t consent to receive notices
              electronically, you must stop using the Services. Unless otherwise
              specified in these Terms, all notices to Graze shall be sent to{" "}
              <a href="mailto:legal@graze.social">legal@graze.social</a>.
              Notices sent to us pursuant to any section of these Terms shall be
              deemed duly served and effective only on actual receipt by us.{" "}
            </li>
            <li>
              <strong>Severability; Waiver.</strong> Graze’s failure to enforce
              any right or provision of these Terms will not be considered a
              waiver of such right or provision. The waiver of any such right or
              provision will be effective only if in writing and signed by a
              duly authorized representative of Graze. Except as expressly set
              forth in these Terms, the exercise by either party of any of its
              remedies under these Terms will be without prejudice to its other
              remedies under these Terms or otherwise. If for any reason a court
              of competent jurisdiction finds any provision of these Terms
              invalid or unenforceable, that provision will be enforced to the
              maximum extent permissible, and the other provisions of these
              Terms will remain in full force and effect.{" "}
            </li>
            <li>
              <strong>Consent to Electronic Communications</strong>. By using
              the Services, you consent to receiving certain electronic
              communications from Graze. You agree that any notices, agreements,
              disclosures, or other communications that Graze sends to you
              electronically will satisfy any legal communication requirements,
              including that those communications be in writing.{" "}
            </li>
            <li>
              <strong>Force Majeure</strong>. Neither party will be liable for,
              or be considered to be in breach of or default under, these Terms
              on account of, any delay or failure to perform as required by
              these Terms as a result of any cause or condition beyond its
              reasonable control, including without limitation unavailability of
              Third-Party Services, so long as that party uses all commercially
              reasonable efforts to avoid or remove the causes of
              non-performance.
            </li>
            <li>
              <strong>Amendments.</strong> Graze may amend these Terms from time
              to time in our sole discretion. All changes are effective
              immediately when we post them and will apply to all access to and
              use of the Services thereafter. Your continued use of the Services
              following the posting of revised Terms means that you accept and
              agree to the changes. If you do not want to agree to the revised
              Terms, you must immediately terminate your use of the Services and
              delete it from your device.
            </li>
          </ol>
        </li>
      </ol>
    </div>
  );
}
